import { useEffect, useMemo, useRef, useState } from 'react';

import Utils from '../../../../../utils/immersive_utils';
import { TransactionDataType } from '../../../../../providers/portfolio-performance/transactions/transactions.types';
import { parseUtcTimestamp } from '../../../../../providers/portfolio-performance/transactions/parse-utc-timestamp';
import { normalizeExecRole } from './exec-role';

interface PortfolioPerformanceTransactionsMetricsProps {
  data: TransactionDataType[];
}

interface TradedMetrics {
  perpNotional: number;
  perpsBought: number;
  perpsSold: number;
  // Split by execution role rather than side — a row only counts here if
  // `user_role` is populated as 'maker'/'taker' (Deribit always sets it;
  // rows from venues that don't report it are excluded from both, so the
  // two can undershoot perpNotional).
  perpMakerVolume: number;
  perpTakerVolume: number;
  optionsVolume: number;
  callsBought: number;
  callsSold: number;
  putsBought: number;
  putsSold: number;
  optionMakerVolume: number;
  optionTakerVolume: number;
  // Fee totals, split the same way as the volumes above. Positive = net
  // fees paid; can go negative if maker rebates outweigh fees charged in
  // the window — see getFee()'s comment for the sign handling.
  perpFeesBought: number;
  perpFeesSold: number;
  perpFeesTotal: number;
  callFeesBought: number;
  callFeesSold: number;
  putFeesBought: number;
  putFeesSold: number;
  optionFeesTotal: number;
  lastTradeTimestamp: number | null;
}

type MetricKey = Exclude<keyof TradedMetrics, 'lastTradeTimestamp'>;

// Deribit reports PERPETUAL, OKX reports SWAP for the same conceptual product.
const PERP_TYPES = new Set(['PERPETUAL', 'SWAP']);

const isBuySide = (side: unknown): boolean =>
  typeof side === 'string' && side.toLowerCase().includes('buy');

const isSellSide = (side: unknown): boolean =>
  typeof side === 'string' && side.toLowerCase().includes('sell');

// Fees are typically sub-satoshi in size (e.g. 1.6e-07 BTC) — 2dp would
// round every fee tile to a permanent 0.00, so fee keys get more precision
// than the volume/notional tiles. Perp volumes are whole-dollar USD
// notionals (no meaningful sub-dollar precision), option volumes are
// contract counts where fractional size is meaningful.
const isFeeKey = (key: MetricKey): boolean => key.toLowerCase().includes('fee');
const isPerpVolumeKey = (key: MetricKey): boolean => key.toLowerCase().startsWith('perp');
const precisionFor = (key: MetricKey): number => {
  if (isFeeKey(key)) return 6;
  if (isPerpVolumeKey(key)) return 0;
  return 2;
};

// Deribit and OKX both suffix option instrument names with -C / -P
// (e.g. "BTC-4JUL26-59500-C"), so one suffix check covers both venues.
const isCallInstrument = (instrument: string): boolean => instrument.endsWith('-C');
const isPutInstrument = (instrument: string): boolean => instrument.endsWith('-P');

// Deribit reports the fee under `commission`, OKX under `fee` — a given row
// only ever has one of the two populated, and — confirmed against live
// data on both venues — they use OPPOSITE sign conventions:
//   Deribit `commission`: positive = taker fee CHARGED (cost), negative =
//     maker rebate CREDITED (verified via 35k+ rows cross-tabulated
//     against `user_role`, and against the Deribit UI's own Fee
//     Charged/Fee Rate figures for a known rebate fill).
//   OKX `fee`: negative = fee CHARGED (cost), positive = rebate CREDITED
//     (verified: every taker fill in 18k+ rows has negative fee, and
//     taker fills can never be a rebate, so negative must mean cost).
// Normalising both to "positive = cost, negative = rebate/credit" lets the
// caller just sum signed values — an account earning more in rebates than
// it pays in fees ends up with a negative "fees paid" total, which is the
// correct (if unusual-looking) answer.
const getFee = (tx: TransactionDataType): number => {
  if (tx.commission !== undefined) {
    const parsed = parseFloat(tx.commission);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (tx.fee !== undefined) {
    const parsed = parseFloat(tx.fee);
    return Number.isFinite(parsed) ? -parsed : 0;
  }
  return 0;
};

export const computeTradedMetrics = (data: TransactionDataType[]): TradedMetrics => {
  const metrics: TradedMetrics = {
    perpNotional: 0,
    perpsBought: 0,
    perpsSold: 0,
    perpMakerVolume: 0,
    perpTakerVolume: 0,
    optionsVolume: 0,
    callsBought: 0,
    callsSold: 0,
    putsBought: 0,
    putsSold: 0,
    optionMakerVolume: 0,
    optionTakerVolume: 0,
    perpFeesBought: 0,
    perpFeesSold: 0,
    perpFeesTotal: 0,
    callFeesBought: 0,
    callFeesSold: 0,
    putFeesBought: 0,
    putFeesSold: 0,
    optionFeesTotal: 0,
    lastTradeTimestamp: null,
  };

  for (const tx of data) {
    if (tx.event_type !== 'trade') continue;

    const amount = Math.abs(parseFloat(tx.amount));
    if (!Number.isFinite(amount)) continue;
    const fee = getFee(tx);
    // Deribit reports role as `user_role` ('maker'/'taker'), OKX as
    // `execType` ('M'/'T') — normalise both via the shared helper (same one
    // the per-row Side tag in transactions-table uses, so they can't drift).
    const execRole = normalizeExecRole(tx.user_role ?? tx.execType);
    const isMaker = execRole === 'maker';
    const isTaker = execRole === 'taker';

    const txDate = parseUtcTimestamp(tx.timestamp);
    const txTime = txDate ? txDate.getTime() : NaN;
    if (
      Number.isFinite(txTime) &&
      (metrics.lastTradeTimestamp === null || txTime > metrics.lastTradeTimestamp)
    ) {
      metrics.lastTradeTimestamp = txTime;
    }

    if (PERP_TYPES.has(tx.instrument_type)) {
      metrics.perpNotional += amount;
      if (isBuySide(tx.side)) {
        metrics.perpsBought += amount;
        metrics.perpFeesBought += fee;
      } else if (isSellSide(tx.side)) {
        metrics.perpsSold += amount;
        metrics.perpFeesSold += fee;
      }
      if (isMaker) metrics.perpMakerVolume += amount;
      else if (isTaker) metrics.perpTakerVolume += amount;
      continue;
    }

    if (tx.instrument_type === 'OPTION') {
      metrics.optionsVolume += amount;
      if (isMaker) metrics.optionMakerVolume += amount;
      else if (isTaker) metrics.optionTakerVolume += amount;
      const instrument = tx.instrument_name || (tx as unknown as { instId?: string }).instId || '';

      if (isCallInstrument(instrument)) {
        if (isBuySide(tx.side)) {
          metrics.callsBought += amount;
          metrics.callFeesBought += fee;
        } else if (isSellSide(tx.side)) {
          metrics.callsSold += amount;
          metrics.callFeesSold += fee;
        }
      } else if (isPutInstrument(instrument)) {
        if (isBuySide(tx.side)) {
          metrics.putsBought += amount;
          metrics.putFeesBought += fee;
        } else if (isSellSide(tx.side)) {
          metrics.putsSold += amount;
          metrics.putFeesSold += fee;
        }
      }
    }
  }

  metrics.perpFeesTotal = metrics.perpFeesBought + metrics.perpFeesSold;
  metrics.optionFeesTotal =
    metrics.callFeesBought + metrics.callFeesSold + metrics.putFeesBought + metrics.putFeesSold;

  return metrics;
};

// Perp notional/volumes are always USD-denominated (industry convention —
// contracts × price in dollar terms — confirmed by precisionFor's 0dp
// perp rule above), regardless of the account's own underlying. Options
// volumes and ALL fees (perp and option alike) are denominated in the
// account's own underlying currency instead (contract-count/native-token
// terms) — taken from the transaction rows' own `currency` field (already
// used the same way in transactions-table.tsx's per-row fee display)
// rather than assumed, so this doesn't hardcode "BTC" for an ETH account.
const getUnderlyingCurrency = (data: TransactionDataType[]): string => {
  const row = data.find((tx) => tx.currency);
  return row?.currency ?? '';
};

const withUnit = (label: string, unit: string): string => (unit ? `${label} (${unit})` : label);

// For labels that already carry a Buy/Sell qualifier in parens — folds the
// unit into the same parenthetical ("Perp Fees (Buy, BTC)") rather than a
// second, separate one.
const withQualifiedUnit = (label: string, qualifier: string, unit: string): string =>
  unit ? `${label} (${qualifier}, ${unit})` : `${label} (${qualifier})`;

const formatTimeAgo = (timestamp: number | null, nowTick: number): string => {
  if (timestamp === null) return '-';
  const seconds = Math.max(0, Math.floor((nowTick - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

const MetricTile = ({
  label,
  value,
  flashDirection,
}: {
  label: string;
  value: string;
  flashDirection: 'up' | 'down' | null;
}) => (
  <div className={`transactions-metric-tile${flashDirection ? ` flash-${flashDirection}` : ''}`}>
    <span className="transactions-metric-label">{label}</span>
    <span className="transactions-metric-value">{value}</span>
  </div>
);

// At-a-glance read on the maker/taker split alongside the exact figures in
// the tiles above — green segment (maker) on the left, red (taker) filling
// the remainder, widths proportional to each side's share of the two
// combined (not of total volume — see roleColumns' own comment on why rows
// with no reported role are excluded from both).
const MakerTakerBar = ({ makerValue, takerValue }: { makerValue: number; takerValue: number }) => {
  const total = makerValue + takerValue;
  if (total <= 0) return null;

  const makerPct = (makerValue / total) * 100;
  const takerPct = 100 - makerPct;

  return (
    <div
      className="maker-taker-bar"
      title={`Maker ${makerPct.toFixed(1)}% / Taker ${takerPct.toFixed(1)}%`}
    >
      <div className="maker-taker-bar-maker" style={{ width: `${makerPct}%` }} />
      <div className="maker-taker-bar-taker" style={{ width: `${takerPct}%` }} />
    </div>
  );
};

export const PortfolioPerformanceTransactionsMetrics = ({
  data,
}: PortfolioPerformanceTransactionsMetricsProps) => {
  const metrics = useMemo(() => computeTradedMetrics(data), [data]);
  const underlying = useMemo(() => getUnderlyingCurrency(data), [data]);

  // Tick every second so "time since last trade" stays live without needing new data.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Flash any tile whose underlying value just changed — the visible signal
  // that a genuinely new trade landed, not just a routine 10s poll no-op.
  // These are rolling-window sums, so they'll almost always go up (a new
  // trade adds to the total) but can drop if an old trade rolls out of the
  // 24h window between polls — colour the flash accordingly either way.
  const previousMetricsRef = useRef<TradedMetrics | null>(null);
  const [flashDirections, setFlashDirections] = useState<Partial<Record<MetricKey, 'up' | 'down'>>>(
    {},
  );
  const flashTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const previous = previousMetricsRef.current;
    previousMetricsRef.current = metrics;

    if (!previous) return;

    const metricKeys = (Object.keys(metrics) as (keyof TradedMetrics)[]).filter(
      (key) => key !== 'lastTradeTimestamp',
    ) as MetricKey[];

    const directions: Partial<Record<MetricKey, 'up' | 'down'>> = {};
    metricKeys.forEach((key) => {
      // Compare the rounded display string, not the raw float — a change
      // too small to show at this precision shouldn't trigger a flash.
      const decimals = precisionFor(key);
      if (
        Utils.formatNumber(metrics[key], decimals) === Utils.formatNumber(previous[key], decimals)
      )
        return;
      directions[key] = metrics[key] > previous[key] ? 'up' : 'down';
    });
    if (Object.keys(directions).length === 0) return;

    setFlashDirections(directions);
    if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = window.setTimeout(() => setFlashDirections({}), 900);
  }, [metrics]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  // Each column pairs a volume/notional tile with its corresponding fee
  // tile directly underneath — same 8 categories as before, just stacked
  // instead of laid end-to-end.
  const columns: {
    volumeKey: MetricKey;
    volumeLabel: string;
    feeKey: MetricKey;
    feeLabel: string;
  }[] = [
    {
      volumeKey: 'perpNotional',
      volumeLabel: withUnit('Perp Volume', 'USD'),
      feeKey: 'perpFeesTotal',
      feeLabel: withUnit('Perp Fees', underlying),
    },
    {
      volumeKey: 'perpsBought',
      volumeLabel: withUnit('Perps Bought', 'USD'),
      feeKey: 'perpFeesBought',
      feeLabel: withQualifiedUnit('Perp Fees', 'Buy', underlying),
    },
    {
      volumeKey: 'perpsSold',
      volumeLabel: withUnit('Perps Sold', 'USD'),
      feeKey: 'perpFeesSold',
      feeLabel: withQualifiedUnit('Perp Fees', 'Sell', underlying),
    },
    {
      volumeKey: 'optionsVolume',
      volumeLabel: withUnit('Options Volume', underlying),
      feeKey: 'optionFeesTotal',
      feeLabel: withUnit('Option Fees', underlying),
    },
    {
      volumeKey: 'callsBought',
      volumeLabel: withUnit('Calls Bought', underlying),
      feeKey: 'callFeesBought',
      feeLabel: withQualifiedUnit('Call Fees', 'Buy', underlying),
    },
    {
      volumeKey: 'callsSold',
      volumeLabel: withUnit('Calls Sold', underlying),
      feeKey: 'callFeesSold',
      feeLabel: withQualifiedUnit('Call Fees', 'Sell', underlying),
    },
    {
      volumeKey: 'putsBought',
      volumeLabel: withUnit('Puts Bought', underlying),
      feeKey: 'putFeesBought',
      feeLabel: withQualifiedUnit('Put Fees', 'Buy', underlying),
    },
    {
      volumeKey: 'putsSold',
      volumeLabel: withUnit('Puts Sold', underlying),
      feeKey: 'putFeesSold',
      feeLabel: withQualifiedUnit('Put Fees', 'Sell', underlying),
    },
  ];

  // Maker/taker split for perps and options — percentage is of the two
  // combined, not of total volume, since rows with no role reported
  // (venues that don't set it) are excluded from both buckets. Rendered as
  // a bar (see MakerTakerBar) rather than the tiles this used to be, in its
  // own row between the tile strip and the Event Type filters.
  const roleColumns: {
    categoryLabel: string;
    makerKey: MetricKey;
    makerLabel: string;
    takerKey: MetricKey;
    takerLabel: string;
  }[] = [
    {
      categoryLabel: withUnit('Perp Maker/Taker', 'USD'),
      makerKey: 'perpMakerVolume',
      makerLabel: 'Maker',
      takerKey: 'perpTakerVolume',
      takerLabel: 'Taker',
    },
    {
      categoryLabel: withUnit('Option Maker/Taker', underlying),
      makerKey: 'optionMakerVolume',
      makerLabel: 'Maker',
      takerKey: 'optionTakerVolume',
      takerLabel: 'Taker',
    },
  ];

  const withPct = (value: number, total: number, decimals: number): string =>
    total > 0
      ? `${Utils.formatNumber(value, decimals)} (${((value / total) * 100).toFixed(1)}%)`
      : Utils.formatNumber(value, decimals);

  return (
    <>
      <div className="transactions-metrics-strip">
        {columns.map(({ volumeKey, volumeLabel, feeKey, feeLabel }) => (
          <div className="transactions-metric-column" key={volumeKey}>
            <MetricTile
              label={volumeLabel}
              value={Utils.formatNumber(metrics[volumeKey], precisionFor(volumeKey))}
              flashDirection={flashDirections[volumeKey] ?? null}
            />
            <MetricTile
              label={feeLabel}
              value={Utils.formatNumber(metrics[feeKey], precisionFor(feeKey))}
              flashDirection={flashDirections[feeKey] ?? null}
            />
          </div>
        ))}
        <div className="transactions-metric-tile transactions-metric-tile-last-trade">
          <span className="transactions-metric-label">Last Trade</span>
          <span className="transactions-metric-value">
            <span className="live-dot" />
            {formatTimeAgo(metrics.lastTradeTimestamp, nowTick)}
          </span>
        </div>
      </div>
      <div className="maker-taker-bars-row">
        {roleColumns.map(({ categoryLabel, makerKey, makerLabel, takerKey, takerLabel }) => {
          const total = metrics[makerKey] + metrics[takerKey];
          return (
            <div className="maker-taker-bar-block" key={makerKey}>
              <span className="maker-taker-bar-title">{categoryLabel}</span>
              <MakerTakerBar makerValue={metrics[makerKey]} takerValue={metrics[takerKey]} />
              <div className="maker-taker-bar-footer">
                <span className="maker-taker-bar-footer-maker">
                  {makerLabel} {withPct(metrics[makerKey], total, precisionFor(makerKey))}
                </span>
                <span className="maker-taker-bar-footer-taker">
                  {takerLabel} {withPct(metrics[takerKey], total, precisionFor(takerKey))}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
